import {setTime, getNextHour, groupBy, stringCompare, nonEmpty, byProp, numericCompareAsc} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import {addMinutes, isSameDay, parseISO} from 'date-fns';
import {
  getBuildingEntities,
  getDefaultBuildingId,
  getAllBuildings,
  Building,
  AttendeeAvailability,
  AttendeeAvailabilityStatus,
  MeetingAttendeeType,
  getUser,
  compareAttendeeAvailabilities,
  getAllMeetingSuggestions,
  getAllWorkspaceReservations,
  InviteeDetailsSelectorProps,
  getUserDirectoryObjectId,
  getStartOfTodayISOString,
  getHasUserGivenCalendarConsent,
  getIsMeetingsCalendarEnabled,
  sortAttendeeAvailabilities,
  getUserClosedConsentTile,
  getIsUserCalendarConsentInvalid,
  getMeetingRoomEntities,
  isMeetingDateTimeRangeValid,
  getFloorsByBuildingId,
  getAvailableMeetingRooms,
  getAllWorkdays,
  getWorkdayByDate,
  MeetingSuggestion,
  getIsGoogleIntegrationActive,
  getIsMicrosoft365IntegrationActive,
} from '@lib/store/src/data';
import {getWorkspaceReservationByDate} from '../../data/workspaceReservation';
import {RootState} from '../../rootReducer';
import createCachedSelector from 're-reselect';
import {getIsHybridMeetingsFeatureEnabled} from '../../infrastructure/features/selectors';
import {MeetingDurationDataSet} from './types';

export const getMeetingDurations = createCachedSelector(
  (state: RootState, type: MeetingDurationDataSet) => ({
    meetingDurations: state.ui.meetings.meetingDurations,
    bookRoomDurations: state.ui.meetings.bookRoomNowDurations,
    type,
  }),
  ({meetingDurations, bookRoomDurations, type}) => {
    switch (type) {
      case 'bookRoomNow':
        return bookRoomDurations;
      case 'newEvent':
      default:
        return meetingDurations;
    }
  },
)((_state, type) => type);

export const getDefaultMeetingDuration = (state: RootState) => state.ui.meetings.defaultMeetingDuration;

export const getNewMeetingBuildingId = (state: RootState) => state.ui.meetings.newMeetingBuildingId;

export const getNewMeeting = (state: RootState) => ({
  ...state.ui.meetings.newMeeting,
  isOnlineMeeting:
    state.data.meetings.settings.onlineMeetingLinkSupported && state.ui.meetings.newMeeting.isOnlineMeeting,
});

export const getRoomAvailabilityPeriodOfInterest = (state: RootState) =>
  state.ui.meetings.roomAvailabilityPeriodOfInterest;

export const getCurrentlySelectedRooms = createSelector(
  getMeetingRoomEntities,
  getNewMeeting,
  (allRoomEntities, newEvent) => nonEmpty(newEvent.roomIds.map((roomId) => allRoomEntities[roomId])),
);
export const getCurrentlySelectedRoomEmails = createSelector(getCurrentlySelectedRooms, (rooms) =>
  rooms.map((room) => room.email),
);

export const getNotSelectedAvailableMeetingRooms = createSelector(
  getAvailableMeetingRooms,
  getNewMeeting,
  (allMeetingRooms, {roomIds}) =>
    nonEmpty(allMeetingRooms.filter((meetingRoom) => !roomIds.includes(meetingRoom.id))).sort(
      byProp('capacity', numericCompareAsc),
    ),
);

export const getSearchResultsAsAttendeeAvailabilities = createSelector(
  getWorkspaceReservationByDate,
  getNewMeeting,
  getUser,
  (state: RootState, date: Date | undefined) => getWorkdayByDate(state, date?.toISOString() ?? ''),
  (
    workspaceReservation,
    {attendees},
    {directoryObjectId, firstName, lastName, email: currentUserEmail, imageHash: currentUserImageHash},
    workday,
  ) => {
    const mappedAsAttendeeAvailabilities = attendees.map(
      ({email, imageHash, initials, name}) =>
        ({
          availability: AttendeeAvailabilityStatus.Unknown,
          displayName: name,
          email,
          imageHash,
          initials,
          locationType: MeetingAttendeeType.Unknown,
        } as AttendeeAvailability),
    );

    const organizer = mappedAsAttendeeAvailabilities.find(
      (availability) => availability.directoryObjectId === directoryObjectId,
    );

    if (organizer) {
      return mappedAsAttendeeAvailabilities;
    }
    const workspaceReservationBuildingId = workspaceReservation?.currentMember?.location.buildingId;

    const locationTypeBasedOnWorkspaceReservation = workspaceReservationBuildingId
      ? MeetingAttendeeType.Local
      : MeetingAttendeeType.Remote;
    const locationTypeBasedOnWorkday = workday?.nodeId
      ? MeetingAttendeeType.Local
      : workday?.status
      ? MeetingAttendeeType.Remote
      : MeetingAttendeeType.Unknown;
    return [
      ...mappedAsAttendeeAvailabilities,
      {
        availability: AttendeeAvailabilityStatus.Unknown,
        directoryObjectId,
        displayName: `${firstName} ${lastName}`,
        email: currentUserEmail,
        imageHash: currentUserImageHash ?? '',
        initials: firstName.charAt(0) ?? '' + lastName.charAt(0) ?? '',
        locationType: locationTypeBasedOnWorkday,
        shiftBuildingId: workday?.nodeId,
        shiftBuildingName: workday?.nodeName,
      },
    ] as AttendeeAvailability[];
  },
);

export const getCurrentAttendeeAvailabilities = createSelector(
  (state: RootState, props: {isDateRangeValid: boolean; date: Date | undefined}) => ({
    isDateRangeValid: props.isDateRangeValid,
    fallbackAvailabilities: getSearchResultsAsAttendeeAvailabilities(state, props.date),
  }),
  (state: RootState) => state.data.meetings.currentAttendeeAvailabilities,
  getUserDirectoryObjectId,
  (
    {isDateRangeValid, fallbackAvailabilities},
    attendeeAvailabilities,
    userDirectoryObjectId,
  ): AttendeeAvailability[] => {
    const activeAvailabilities = isDateRangeValid ? attendeeAvailabilities : fallbackAvailabilities;
    return sortAttendeeAvailabilities(activeAvailabilities, userDirectoryObjectId, true);
  },
);

export const getIsMeetingTimeSelected = createSelector(
  getNewMeeting,
  ({selectedStartDateTime, selectedEndDateTime}) => !!selectedStartDateTime || !!selectedEndDateTime,
);
export const getIsMeetingDateRangeValid = createSelector(
  getNewMeeting,
  ({selectedStartDateTime, selectedEndDateTime}) =>
    !!selectedStartDateTime &&
    !!selectedEndDateTime &&
    isMeetingDateTimeRangeValid(parseISO(selectedStartDateTime), parseISO(selectedEndDateTime)),
);

export const getNewMeetingManualDateTimeFallbacks = createCachedSelector(
  (_: RootState, dateISOString: string | undefined) => dateISOString,
  getStartOfTodayISOString,
  (dateISOString, todayISOString) => {
    if (!dateISOString) {
      const guardValue = new Date().toISOString();
      return {
        manualStartDateTimeFallback: guardValue,
        manualEndDateTimeFallback: guardValue,
      };
    }
    const date = parseISO(dateISOString);
    const today = parseISO(todayISOString);
    const isToday = isSameDay(date, today);
    const startDateTime = isToday ? getNextHour() : setTime(date, 10, 0);

    const endDateTime = addMinutes(startDateTime, 30);

    return {
      manualStartDateTimeFallback: startDateTime.toISOString(),
      manualEndDateTimeFallback: endDateTime.toISOString(),
    };
  },
)((_state, date) => date || '');

export const getFallbackBuildingForNewMeeting = createSelector(
  getBuildingEntities,
  getDefaultBuildingId,
  (state: RootState, date: string | undefined) =>
    getWorkspaceReservationByDate(state, date ? parseISO(date) : undefined),
  getWorkdayByDate,
  (buildingEntities, defaultBuildingId, workspaceReservation, workday) => {
    if (!buildingEntities) {
      return;
    }
    const buildingId = workday?.nodeId;
    return buildingEntities[buildingId ?? ''] ?? buildingEntities[defaultBuildingId];
  },
);

export const getSelectedNewMeetingBuilding = createSelector(
  getAllBuildings,
  getBuildingEntities,
  getNewMeetingBuildingId,
  getFallbackBuildingForNewMeeting,
  (allBuildings, buildingEntities, newMeetingBuildingId, fallbackBuilding): Building | undefined => {
    if (!buildingEntities) {
      return;
    }

    return buildingEntities[newMeetingBuildingId] ?? fallbackBuilding ?? allBuildings[0];
  },
);

export const getFloorsByBuildingIdSorted = (buildingId: string) => (state: RootState) =>
  getFloorsByBuildingId(state, buildingId).sort(byProp('number', numericCompareAsc));

export const getAllMeetingSuggestionsWithOrganizerAttendees = createSelector(
  getAllMeetingSuggestions,
  getUser,
  getAllWorkspaceReservations,
  getAllWorkdays,
  (
    meetingSuggestions,
    {directoryObjectId, email, firstName, lastName, imageHash},
    workspaceReservations,
    workdays,
  ): MeetingSuggestion[] =>
    meetingSuggestions.map((suggestion) => {
      const {buildingId: userWorkspaceReservationBuildingId, buildingName: userWorkspaceReservationBuildingName} =
        workspaceReservations.find((workspaceReservation) =>
          isSameDay(parseISO(workspaceReservation.startDate), parseISO(suggestion.timeSlotStart)),
        ) ?? {};
      const locationTypeBasedOnWorkspaceReservation = userWorkspaceReservationBuildingId
        ? MeetingAttendeeType.Local
        : MeetingAttendeeType.Remote;

      const {
        nodeId: userWorkdayBuildingId,
        nodeName: userWorkdayBuildingName,
        status: workdayStatus,
      } = workdays.find((workday) => isSameDay(parseISO(workday.startDate), parseISO(suggestion.timeSlotStart))) ?? {};
      const locationTypeBasedOnWorkday = userWorkdayBuildingId
        ? MeetingAttendeeType.Local
        : workdayStatus
        ? MeetingAttendeeType.Remote
        : MeetingAttendeeType.Unknown;

      return {
        ...suggestion,
        attendeeAvailabilities: [
          ...suggestion.attendeeAvailabilities,
          {
            directoryObjectId,
            email,
            availability: AttendeeAvailabilityStatus.Available,
            displayName: `${firstName} ${lastName}`,
            locationType: locationTypeBasedOnWorkday,
            shiftBuildingId: userWorkdayBuildingId,
            shiftBuildingName: userWorkdayBuildingName,
            initials: (firstName?.[0] ?? '') + (lastName?.[0] ?? ''),
            imageHash,
          } as AttendeeAvailability,
        ],
      };
    }),
);

export const getTimeSuggestionById = createSelector(
  (state: RootState, props: InviteeDetailsSelectorProps) => ({
    suggestions: getAllMeetingSuggestionsWithOrganizerAttendees(state),
    selectedSuggestionId: props.selectedTimeSuggestionId,
  }),
  ({suggestions, selectedSuggestionId}) => {
    return suggestions.find((suggestion) => suggestion.id === selectedSuggestionId);
  },
);

export const getAttendeeAvailabilitiesForSource = createSelector(
  (state: RootState, props: InviteeDetailsSelectorProps) => ({
    meetingSuggestion: getTimeSuggestionById(state, props),
    manualAttendeeAvailabilities: getCurrentAttendeeAvailabilities(state, {
      isDateRangeValid: props.isDateRangeValid ?? false,
      date: props.date,
    }),
    source: props.source,
  }),
  ({meetingSuggestion, manualAttendeeAvailabilities, source}): AttendeeAvailability[] => {
    const availabilities: AttendeeAvailability[] =
      source === 'manual' ? manualAttendeeAvailabilities ?? [] : meetingSuggestion?.attendeeAvailabilities ?? [];

    return [...availabilities].sort(compareAttendeeAvailabilities);
  },
);

export const getAttendeeAvailibilitiesGrouppedByLocationType = createSelector(
  getAttendeeAvailabilitiesForSource,
  (availabilities) => groupBy(availabilities, (attendee) => attendee.locationType),
);

export const getLocalGroupOfAttendeeAvailabilitiesAsBuildingGroups = createSelector(
  (state: RootState, props: InviteeDetailsSelectorProps) =>
    getAttendeeAvailibilitiesGrouppedByLocationType(state, props),
  (groupedAttendeeAvailabilities) => {
    const localGroup = groupedAttendeeAvailabilities?.Local;
    if (!localGroup) {
      return {};
    }
    const localGroups = groupBy(localGroup, (attendee) => attendee.shiftBuildingId);

    const localGroupsAsEntriesSorted = Object.entries(localGroups).sort((first, second) =>
      stringCompare(first[1]?.[0]?.shiftBuildingName, second[1]?.[0]?.shiftBuildingName),
    );

    return localGroupsAsEntriesSorted.reduce(
      (acc, current) => ({
        ...acc,
        [current[0]]: current[1],
      }),
      {},
    );
  },
);

export const getAttendeeAvailabilitiesGroupedByResponseStatusAndBuilding = createSelector(
  (state: RootState, props: InviteeDetailsSelectorProps) => ({
    groupedAttendeeAvailabilities: getAttendeeAvailibilitiesGrouppedByLocationType(state, props),
    localGroupedByBuildingId: getLocalGroupOfAttendeeAvailabilitiesAsBuildingGroups(state, props),
  }),
  ({groupedAttendeeAvailabilities, localGroupedByBuildingId}): {[index: string]: AttendeeAvailability[]} | {} => {
    const remoteAttendees = groupedAttendeeAvailabilities.Remote;
    const withRemoteGroup = remoteAttendees?.length
      ? {
          ...localGroupedByBuildingId,
          [MeetingAttendeeType.Remote]: remoteAttendees,
        }
      : localGroupedByBuildingId;

    const unknownGroup = groupedAttendeeAvailabilities.Unknown;
    return unknownGroup
      ? {
          ...withRemoteGroup,
          [MeetingAttendeeType.Unknown]: unknownGroup,
        }
      : withRemoteGroup;
  },
);

export const getGroupedAndSortedAvailabilities = createSelector(
  getAttendeeAvailabilitiesGroupedByResponseStatusAndBuilding,
  getUserDirectoryObjectId,
  (allGroups, loggedInUserDirectoryObjectId): {[index: string]: AttendeeAvailability[]} | {} =>
    Object.entries(allGroups).reduce(
      (acc, current) => ({
        ...acc,
        [current[0]]: sortAttendeeAvailabilities(current[1], loggedInUserDirectoryObjectId),
      }),
      {},
    ),
);

export const getIsHMEnabledAndIntegrationEnabled = createSelector(
  getIsMeetingsCalendarEnabled,
  getIsHybridMeetingsFeatureEnabled,
  (isMeetingsCalendarEnabled, isHybridMeetingsFeatureEnabled) => {
    return isMeetingsCalendarEnabled && isHybridMeetingsFeatureEnabled;
  },
);

export const getIsMeetingsCalendarActive = createSelector(
  getHasUserGivenCalendarConsent,
  getIsMeetingsCalendarEnabled,
  getIsHybridMeetingsFeatureEnabled,
  (hasUserGivenCalendarConsent, isMeetingsCalendarEnabled, isHybridMeetingsFeatureEnabled) => {
    return hasUserGivenCalendarConsent && isMeetingsCalendarEnabled && isHybridMeetingsFeatureEnabled;
  },
);

export const getIsConsentTileVisible = createSelector(
  getIsHybridMeetingsFeatureEnabled,
  getIsMeetingsCalendarEnabled,
  getHasUserGivenCalendarConsent,
  getUserClosedConsentTile,
  (isHybridMeetingsFeatureEnabled, isMeetingsCalendarEnabled, hasUserGivenCalendarConsent, isUserConsentTileClosed) => {
    return (
      isHybridMeetingsFeatureEnabled &&
      isMeetingsCalendarEnabled &&
      !hasUserGivenCalendarConsent &&
      !isUserConsentTileClosed
    );
  },
);

export const getIsInvalidConsentTileVisible = createSelector(
  getIsHybridMeetingsFeatureEnabled,
  getIsMeetingsCalendarEnabled,
  getHasUserGivenCalendarConsent,
  getIsUserCalendarConsentInvalid,
  (
    isHybridMeetingsFeatureEnabled,
    isMeetingsCalendarEnabled,
    hasUserGivenCalendarConsent,
    isUserCalendarConsentInvalid,
  ) => {
    return (
      isHybridMeetingsFeatureEnabled &&
      isMeetingsCalendarEnabled &&
      hasUserGivenCalendarConsent &&
      isUserCalendarConsentInvalid
    );
  },
);

export const getShouldShowSyncCalendarCard = createSelector(
  getIsHybridMeetingsFeatureEnabled,
  getIsMeetingsCalendarEnabled,
  getHasUserGivenCalendarConsent,
  getUserClosedConsentTile,
  getIsUserCalendarConsentInvalid,
  (
    isHybridMeetingsFeatureEnabled,
    isMeetingsCalendarEnabled,
    hasUserGivenCalendarConsent,
    isUserConsentTileClosed,
    isUserCalendarConsentInvalid,
  ) => {
    // Only show tile allowed by flag and activated by admin
    if (isHybridMeetingsFeatureEnabled && isMeetingsCalendarEnabled) {
      // Green tile: Only show if tile has not been closed and no consent has been given
      if (!hasUserGivenCalendarConsent) {
        if (!isUserConsentTileClosed) {
          return true;
        }
      } else {
        // Red tile: Only show if tile has not been closed and consent is invalid
        if (isUserCalendarConsentInvalid) {
          return true;
        }
      }
    }
    // Default we don't show any tile
    return false;
  },
);

export const getCalendarIntegrationType = createSelector(
  getIsGoogleIntegrationActive,
  getIsMicrosoft365IntegrationActive,
  (google, microsoft) => {
    if (google) return 'Google Calendar';
    else if (microsoft) return 'Microsoft 365';
    else return null;
  },
);
